import type { ReviewScene } from './edits';

/**
 * The answer a scene grades against, whichever columns its mechanic keeps it in — GEN-49.
 *
 * `editableItems` does this for labels, and stops there because a label is the answer only
 * for the kinds whose answer is a word. A SLIDER's is `targetMin`/`targetMax` on a track of
 * `min`/`max`/`step`; an ALLOCATE's is `correctAmount` per category against a `poolTotal`.
 * The console showed none of them, so a generated band nobody could reach published as
 * readily as a right one.
 *
 * Read here rather than switched on in the form, for the reason `edits.ts` gives: an arm
 * per scene kind inside a component grows one every time a kind is added.
 */

type AnswerKey =
  'min' | 'max' | 'step' | 'targetMin' | 'targetMax' | 'poolTotal';

export interface AnswerField {
  key: AnswerKey;
  /** What a reviewer calls it. The column name is not shown. */
  label: string;
  /**
   * `undefined` where the column is empty, and the field is still listed — GEN-78.
   *
   * The same argument `sceneBody` makes: a question that has just become a Slider has no
   * track at all, and it is the one somebody most needs to type a track into. A list built
   * only from the values that are there cannot fill the ones that are not.
   */
  value: number | undefined;
}

export const ANSWER_LABELS: Record<AnswerKey, string> = {
  min: 'Track starts at',
  max: 'Track ends at',
  step: 'Moves in steps of',
  targetMin: 'Correct from',
  targetMax: 'Correct up to',
  poolTotal: 'Total to divide',
};

const field = (key: AnswerKey, value: number | undefined): AnswerField => ({
  key,
  label: ANSWER_LABELS[key],
  value,
});

/** The numeric answer fields on a scene, in the order a reviewer reads them. */
export function answerFields(scene: ReviewScene): AnswerField[] {
  if ('targetMin' in scene && 'min' in scene)
    return [
      field('min', scene.min),
      field('max', scene.max),
      field('step', scene.step),
      field('targetMin', scene.targetMin),
      field('targetMax', scene.targetMax),
    ];

  if ('poolTotal' in scene) return [field('poolTotal', scene.poolTotal)];

  return [];
}

/** The unit a slider's numbers are in, when it has one. */
export function answerUnit(scene: ReviewScene): string | undefined {
  return 'unit' in scene ? scene.unit : undefined;
}

/**
 * One keyed item, under the columns a re-key sends it back under — GEN-77.
 *
 * Three optional columns rather than one value, because which one a scene keys on is its
 * mechanic's business and the caller is diffing against whichever the proposal named. A
 * BUCKET's `correctBucketId` is not here: `updateSceneSchema` cannot re-key it, so showing
 * a previous value would offer a way back from a write nobody can make.
 */
export interface KeyedAnswer {
  id: string;
  label: string;
  // Explicitly `| undefined`: under `exactOptionalPropertyTypes` an absent key and a key
  // set to nothing are different types, and every producer here reads a nullable column.
  isCorrect?: boolean | undefined;
  correctPosition?: number | undefined;
  correctAmount?: number | undefined;
}

/** Each keyed item on a scene, whichever key its mechanic grades against. */
export function keyedAnswers(scene: ReviewScene): KeyedAnswer[] {
  if ('options' in scene)
    return scene.options.map((option) => ({
      id: option.id,
      label: option.label,
      isCorrect: option.isCorrect,
    }));

  if ('categories' in scene)
    return scene.categories.map((category) => ({
      id: category.id,
      label: category.label,
      correctAmount: category.correctAmount,
    }));

  if ('items' in scene)
    return scene.items.map((item) => ({
      id: item.id,
      label: item.label,
      correctPosition: item.correctPosition,
    }));

  return [];
}

export interface KeyedAmount {
  id: string;
  label: string;
  amount: number;
  /** How far either side of the amount still counts. */
  tolerance: number;
}

/**
 * Each ALLOCATE category, the example split, and the range around it.
 *
 * The guard is the whole of it and is load-bearing, which is why this is not
 * `keyedAnswers(...).filter(...)`: only an ALLOCATE keys an amount, and a category nobody
 * has keyed yet still has to appear — that unset field is what GEN-49 put on the screen.
 */
export function keyedAmounts(scene: ReviewScene): KeyedAmount[] {
  if (!('categories' in scene)) return [];

  return scene.categories.map((category) => ({
    id: category.id,
    label: category.label,
    amount: category.correctAmount ?? 0,
    tolerance: category.amountTolerance ?? 0,
  }));
}

/**
 * Which bucket each BUCKET item belongs in, by label.
 *
 * Read-only: reassigning an item is a different interaction from correcting a number, and
 * GEN-49 asks only that a reviewer can see it. An item with no bucket is absent rather
 * than mapped to a placeholder, so "unsorted" stays visibly unsorted — that is the state
 * `unkeyedItems` refuses to publish.
 */
export function bucketLabels(scene: ReviewScene): Map<string, string> {
  if (!('buckets' in scene) || !('items' in scene)) return new Map();

  const byId = new Map<string, string>(
    scene.buckets.map((one) => [one.id, one.label]),
  );
  const pairs: [string, string][] = [];

  for (const item of scene.items) {
    const label =
      item.correctBucketId === undefined
        ? undefined
        : byId.get(item.correctBucketId);

    if (label !== undefined) pairs.push([item.id, label]);
  }

  return new Map(pairs);
}
